import { getNonce as apiGetNonce } from '@exodus/ethereum-api';
const createEVMDepsFactory = ({ analytics, blockchainMetadata, }) => {
    return ({ addEthereumChain, assetName, chainId: _chainId, origin, getAsset, getAddress: _getAddress, getSupportedChainIds, getFeeData: getFeeConfig, getCustomFeeRate, getActiveWalletAccount, simulateEthereumTransactions, getActiveWalletAccountData, transactionSigner, messageSigner, onEthWalletWatchAssetRequest: _onEthWalletWatchAssetRequest, }) => {
        const getServer = async () => {
            const asset = await getAsset(assetName);
            return asset.server;
        };
        const forwardRequest = async ({ method, params, }) => {
            const server = await getServer();
            if (server.proxyToCoinNode) {
                return server.proxyToCoinNode({ method, params });
            }
            const request = server.buildRequest({ method, params });
            return server.sendRequest(request);
        };
        const sendRawTransaction = async (rawTransaction) => {
            const server = await getServer();
            return server.sendRawTransaction(rawTransaction);
        };
        const getAddress = async () => {
            return _getAddress(assetName);
        };
        const getNonce = async () => {
            const asset = await getAsset(assetName);
            const [baseAssetTxLog, blockchainNonce] = await Promise.all([
                blockchainMetadata.getTxLog({
                    assetName,
                    walletAccount: await getActiveWalletAccount(),
                }),
                apiGetNonce({ asset, address: await getAddress() }),
            ]);
            const walletNonce = Array.from(baseAssetTxLog)
                .filter((tx) => tx.sent && !tx.dropped && tx.data.nonce != null)
                .reduce((nonce, tx) => Math.max(tx.data.nonce + 1, nonce), 0);
            return `0x${Math.max(walletNonce, blockchainNonce).toString(16)}`;
        };
        const getEstimatedGas = async (transaction) => {
            return forwardRequest({
                method: 'eth_estimateGas',
                params: [transaction],
            });
        };
        const getFeeData = async () => {
            const { eip1559Enabled, tipGasPrice, gasPrice: _gasPrice, } = await getFeeConfig({
                assetName,
            });
            // gasPrice is actually maxFeePerGas if eip1559 is true
            const gasPrice = `0x${_gasPrice.toBaseNumber().toString(16)}`;
            const feeData = {
                gasPrice,
            };
            if (eip1559Enabled) {
                feeData.maxPriorityFeePerGas = `0x${tipGasPrice
                    .toBaseNumber()
                    .toString(16)}`;
                feeData.maxFeePerGas = gasPrice;
            }
            return feeData;
        };
        const getCustomFeeData = async () => {
            const _maxFeePerGas = await getCustomFeeRate?.();
            if (!_maxFeePerGas) {
                // The user did not select a custom fee rate or
                // we didn't supply the function, eitherway nothing should happen.
                return undefined;
            }
            const { eip1559Enabled, baseFeePerGas } = await getFeeConfig({
                assetName,
            });
            const maxFeePerGas = `0x${_maxFeePerGas.toBaseNumber().toString(16)}`;
            const feeData = {
                // If no eip1559 support, so set it to the max
                // Otherwise ignored
                gasPrice: maxFeePerGas,
            };
            if (eip1559Enabled) {
                feeData.maxFeePerGas = maxFeePerGas;
                // If our custom fee rate is higher than the base fee rate
                // then compute the priority / tip fee.
                if (baseFeePerGas && _maxFeePerGas.gt(baseFeePerGas)) {
                    const maxPriorityFeePerGas = _maxFeePerGas.sub(baseFeePerGas);
                    feeData.maxPriorityFeePerGas = `0x${maxPriorityFeePerGas
                        .toBaseNumber()
                        .toString(16)}`;
                }
            }
            return feeData;
        };
        const trackWalletWatchAssetUse = _onEthWalletWatchAssetRequest
            ? _onEthWalletWatchAssetRequest
            : async (assetDetails) => {
                // TODO: do we still need this? If yes, this ...assetDetails expansion is no bueno
                // why is this not always returning true in browser?
                analytics.track({
                    event: 'DappWalletWatchAsset',
                    properties: {
                        ...assetDetails,
                        dappDomain: origin,
                    },
                });
                return true;
            };
        return {
            addEthereumChain,
            chainId: _chainId || 'undefined',
            forwardRequest,
            sendRawTransaction,
            getAddress,
            getNonce,
            getFeeData,
            getEstimatedGas,
            getCustomFeeData,
            getSupportedChainIds,
            onEthWalletWatchAssetRequest: trackWalletWatchAssetUse,
            simulateEthereumTransactions,
            transactionSigner,
            messageSigner,
            getActiveWalletAccountData,
        };
    };
};
export default createEVMDepsFactory;
